/**
 * OCR digitisation of a paper register (backend: internal/twin/ocr.go + images.go
 * ScanRegister, migrations 0074/0075).
 *
 * On-device OCR (Apple Vision / ML Kit) is the mobile default; the WEB has no
 * on-device OCR, so it uses the Claude-vision escalation: store the register scan →
 * enqueue a job → poll for the brain's draft → pre-fill the register form for review.
 * The platform makes ZERO Anthropic calls — a self-hosted PC running Claude Code on a
 * flat subscription does the vision and writes the draft back (tools/ocr-brain/).
 */

import { api } from "@/lib/api-client";
import type { ImageDatapoint } from "./images";

export type OCRStatus = "pending" | "claimed" | "done" | "failed";

export interface OCRJob {
  id: string;
  register_id: string | null;
  image_id: string;
  status: OCRStatus;
  /** Extracted { metric: value } — present when status === "done". */
  draft?: Record<string, string | number> | null;
  model?: string;
  error?: string;
  created_at: string;
  done_at?: string | null;
}

export interface RegisterScanInput {
  file: Blob;
  compressed?: boolean;
  width?: number;
  height?: number;
  filename?: string;
}

export const ocr = {
  /** Store a register scan photo (multipart) → the image datapoint (carries image_id). */
  scan: (registerId: string, input: RegisterScanInput) => {
    const fd = new FormData();
    fd.append("file", input.file, input.filename ?? "scan");
    fd.append("compressed", input.compressed ? "true" : "false");
    if (input.width) fd.append("width", String(input.width));
    if (input.height) fd.append("height", String(input.height));
    return api.postForm<ImageDatapoint>(`/v1/twin/registers/${registerId}/scan`, fd);
  },

  /** Enqueue a Claude-vision OCR job for a stored scan. */
  enqueue: (registerId: string, imageId: string) =>
    api.post<OCRJob>(`/v1/twin/registers/${registerId}/ocr`, { image_id: imageId }),

  /** Poll a job's status + draft. */
  job: (jobId: string) => api.get<OCRJob>(`/v1/twin/ocr-jobs/${jobId}`),
};
